'use client';

import {
  useEffect,
  useState,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import type { IMQASDKConfig, IMQA } from '@imqa/web-agent';

// Define the type for IMQA configuration
// interface IMQAConfig {
//   collectorUrl: string;
//   serviceName: string;
//   serviceVersion: string;
//   serviceKey: string;
//   consoleCapture?: boolean;
//   advancedNetworkCapture?: boolean;
//   disableReplay?: boolean;
//   captureMetrics?: boolean;
//   debug?: boolean;
//   samplingProbability?: number;
// }

// Default IMQA configuration
export const DEFAULT_IMQA_CONFIG: IMQASDKConfig = {
  collectorUrl: '',
  serviceName: '',
  serviceVersion: '',
  serviceKey: '',
  consoleCapture: false,
  advancedNetworkCapture: false,
  disableReplay: true,
  debug: true,
  samplingProbability: 1,
  captureMetrics: false,
  disablePreflight: false,
  exporterProtocol: 'json',
  sessionStorage: 'cookie',
  // Advanced WebVitals settings
  webVitals: true,
  // Advanced socket settings
  websocket: false,
  socketio: false,
  // Advanced recording settings
  recordCanvas: false,
  maskAllInputs: true,
  maskAllText: false,
  maskClass: '',
  ignoreClass: '',
  blockClass: '',
  ignoreUrls: [],
  tracePropagationTargets: [],
  // Advanced instrumentations settings - sync with main config
  instrumentations: {
    console: true,
    document: true,
    errors: true,
    fetch: true,
    interactions: true,
    longtask: true,
    visibility: true,
    connectivity: true,
    postload: true,
    socketio: false,
    websocket: false,
    webvitals: true, // synced with webVitals
    xhr: true,
  },
};

// Define WebView parameters interface
interface WebViewParams {
  isWebView: boolean;
  sessionId: string;
  serviceName: string;
  serviceKey: string;
  serviceVersion: string;
  collectorUrl: string;
  isInstrumented: boolean;
  isDeactivated: boolean;
}

// Define the shape of the context
interface IMQAContextType {
  config: IMQASDKConfig;
  isInitialized: boolean;
  webViewParams: WebViewParams;
  autoReinit: boolean;
  updateConfig: (newConfig: Partial<IMQASDKConfig>) => void;
  initIMQA: () => void;
  deinitIMQA: () => void;
  toggleFeature: (
    feature: keyof IMQASDKConfig,
    value: boolean | number | string,
  ) => void;
  setAutoReinit: (value: boolean) => void;
  resetToDefaults: () => void;
  clearLocalStorage: () => void;
}

// Create the context with default values
const IMQAContext = createContext<IMQAContextType>({
  config: DEFAULT_IMQA_CONFIG,
  isInitialized: false,
  webViewParams: {
    isWebView: false,
    sessionId: '',
    serviceName: '',
    serviceKey: '',
    serviceVersion: '',
    collectorUrl: '',
    isInstrumented: false,
    isDeactivated: false,
  },
  autoReinit: true,
  updateConfig: () => {},
  initIMQA: () => {},
  deinitIMQA: () => {},
  toggleFeature: () => {},
  setAutoReinit: () => {},
  resetToDefaults: () => {},
  clearLocalStorage: () => {},
});

// Custom hook to use the IMQA context
export const useIMQA = () => useContext(IMQAContext);

interface IMQAProviderProps {
  children: ReactNode;
}

// Helper function to extract IMQA config from URL search params
const getConfigFromUrl = (): Partial<IMQASDKConfig> | null => {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const urlConfig: Partial<IMQASDKConfig> = {};
    let hasAny = false;

    const stringKeys = [
      'collectorUrl',
      'serviceName',
      'serviceVersion',
      'serviceKey',
      'serviceNamespace',
      'deploymentEnvironment',
    ] as const;

    for (const key of stringKeys) {
      const value = params.get(key);
      if (value) {
        (urlConfig as Record<string, unknown>)[key] = value;
        hasAny = true;
      }
    }

    return hasAny ? urlConfig : null;
  } catch {
    return null;
  }
};

// Helper function to get initial config from localStorage
const getInitialConfig = (): IMQASDKConfig => {
  // Only try to read from localStorage if we're in the browser
  if (typeof window !== 'undefined') {
    // URL params take highest priority
    const urlConfig = getConfigFromUrl();

    try {
      const savedConfig = localStorage.getItem('imqaConfig');
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig) as Partial<IMQASDKConfig>;
        console.log('Loading initial config from localStorage:', parsedConfig);

        // Merge with default config to ensure all properties exist
        const mergedConfig = {
          ...DEFAULT_IMQA_CONFIG,
          ...parsedConfig,
          instrumentations: {
            ...DEFAULT_IMQA_CONFIG.instrumentations,
            ...parsedConfig.instrumentations,
          },
        };

        // Sync duplicate settings
        if (parsedConfig.webVitals !== undefined) {
          mergedConfig.instrumentations.webvitals = parsedConfig.webVitals;
        }
        if (parsedConfig.websocket !== undefined) {
          mergedConfig.instrumentations.websocket = parsedConfig.websocket;
        }
        if (parsedConfig.socketio !== undefined) {
          mergedConfig.instrumentations.socketio = parsedConfig.socketio;
        }

        // Override with URL params if present
        if (urlConfig) {
          console.log('Overriding config with URL params:', urlConfig);
          Object.assign(mergedConfig, urlConfig);
        }

        return mergedConfig;
      }
    } catch (error) {
      console.error('Failed to parse saved config, using defaults:', error);
    }

    // No saved config but URL params exist
    if (urlConfig) {
      console.log('Initializing config from URL params:', urlConfig);
      return { ...DEFAULT_IMQA_CONFIG, ...urlConfig };
    }
  }

  return DEFAULT_IMQA_CONFIG;
};

// Helper function to get initial autoReinit setting from localStorage
const getInitialAutoReinit = (): boolean => {
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('imqaAutoReinit');
      if (saved !== null) {
        return JSON.parse(saved) as boolean;
      }
    } catch {
      // ignore
    }
  }
  return true; // default to true (auto reinit enabled)
};

// Provider component to wrap around the application
export const IMQAProvider = ({ children }: IMQAProviderProps) => {
  const [config, setConfig] = useState<IMQASDKConfig>(getInitialConfig);

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [IMQA, setIMQA] = useState<IMQA | null>(null);
  const [autoReinit, setAutoReinitState] = useState<boolean>(getInitialAutoReinit);
  // Guard: prevent auto-reinit immediately after manual deinit
  const manualDeinitRef = useRef(false);
  const [webViewParams, setWebViewParams] = useState<WebViewParams>({
    isWebView: false,
    sessionId: '',
    serviceName: '',
    serviceKey: '',
    serviceVersion: '',
    collectorUrl: '',
    isInstrumented: false,
    isDeactivated: false,
  });

  // Check for WebView parameters and initialize if present
  useEffect(() => {
    const checkWebViewParams = () => {
      const win = window as unknown as {
        __imqa_session_id?: string;
        __imqa_service_name?: string;
        __imqa_service_version?: string;
        __imqa_service_key?: string;
        __imqa_collector_url?: string;
        __imqa_shared_session?: boolean;
        __imqa_deactivated?: boolean;
        IMQA?: { init: (config: IMQASDKConfig) => void; deinit: () => void };
      };
      const isWebView = typeof win.__imqa_session_id !== 'undefined';

      if (isWebView) {
        const params = {
          isWebView: true,
          sessionId: win.__imqa_session_id || '',
          serviceName: win.__imqa_service_name || '',
          serviceKey: win.__imqa_service_key || '',
          serviceVersion: win.__imqa_service_version || '',
          collectorUrl: win.__imqa_collector_url || '',
          isInstrumented: win.__imqa_shared_session === true,
          isDeactivated: win.__imqa_deactivated === true,
        };

        setWebViewParams(params);

        // If WebView parameters are present and not deactivated, initialize IMQA
        if (!params.isDeactivated) {
          setConfig(prevConfig => ({
            ...prevConfig,
            collectorUrl: params.collectorUrl,
            serviceName: params.serviceName,
            serviceKey: params.serviceKey,
            serviceVersion: params.serviceVersion,
          }));
        }
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkWebViewParams()) {
      return;
    }

    // Poll for WebView params (Android bridge might inject after page load)
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = setInterval(() => {
      attempts++;
      if (checkWebViewParams() || attempts >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, 100);

    return () => clearInterval(pollInterval);
  }, []);

  // Load IMQA module once on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadIMQA = async () => {
      try {
        // Import IMQA dynamically to avoid SSR issues
        const IMQAModule = await import('@imqa/web-agent');
        setIMQA(IMQAModule.default);
        console.log('IMQA module loaded successfully');
      } catch (error) {
        console.error('Failed to load IMQA module:', error);
      }
    };

    loadIMQA();
  }, []);

  // Auto-initialize IMQA when config changes and module is loaded
  useEffect(() => {
    if (!IMQA || typeof window === 'undefined') return;

    // Skip auto-initialization if autoReinit is disabled
    if (!autoReinit) {
      console.log('Auto-reinit disabled, skipping auto-initialization');
      return;
    }

    // Check if we have required config values
    const hasRequiredValues = !!(
      config.collectorUrl &&
      config.serviceName &&
      config.serviceVersion &&
      config.serviceKey
    );

    console.log('Config changed - checking for auto-initialization:', {
      collectorUrl: !!config.collectorUrl,
      serviceName: !!config.serviceName,
      serviceVersion: !!config.serviceVersion,
      serviceKey: !!config.serviceKey,
      hasAllRequired: hasRequiredValues,
      currentlyInitialized: isInitialized,
      autoReinit
    });

    if (hasRequiredValues && !isInitialized) {
      // Skip auto-reinit if user just manually deinited
      if (manualDeinitRef.current) {
        console.log('Skipping auto-reinit after manual deinit');
        manualDeinitRef.current = false;
        return;
      }
      console.log('Auto-initializing IMQA with config:', config);
      try {
        IMQA.init(config);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to auto-initialize IMQA:', error);
      }
    } else if (!hasRequiredValues && isInitialized) {
      console.log('Required config values missing, deinitializing IMQA');
      try {
        IMQA.deinit();
        setIsInitialized(false);
      } catch (error) {
        console.error('Failed to deinitialize IMQA:', error);
      }
    }
  }, [config, IMQA, isInitialized, autoReinit]);

  // Save config to localStorage whenever it changes (after initial load)
  useEffect(() => {
    // Simple approach: always save after component is mounted
    if (typeof window === 'undefined') return;

    try {
      const configString = JSON.stringify(config);
      localStorage.setItem('imqaConfig', configString);
      console.log('Saved config to localStorage:', {
        config,
        configString: configString.substring(0, 200) + '...'
      });
    } catch (error) {
      console.warn('Failed to save IMQA config to localStorage:', error);
    }
  }, [config]);

  // Update isInitialized state based on actual IMQA status
  useEffect(() => {
    if (typeof window === 'undefined' || !IMQA) return;

    const checkInitializedStatus = () => {
      // Don't sync state right after manual deinit to avoid re-triggering auto-init
      if (manualDeinitRef.current) return;
      const actualInitialized = IMQA.inited === true;
      if (actualInitialized !== isInitialized) {
        console.log('Syncing isInitialized state:', actualInitialized);
        setIsInitialized(actualInitialized);
      }
    };

    // Check immediately
    checkInitializedStatus();

    // Check periodically
    const interval = setInterval(checkInitializedStatus, 1000);
    return () => clearInterval(interval);
  }, [IMQA, isInitialized]);

  // Update config and save to localStorage
  const updateConfig = (newConfig: Partial<IMQASDKConfig>) => {
    console.log('updateConfig called with:', newConfig);
    const updatedConfig = { ...config, ...newConfig };

    // Sync duplicate settings between performance and feature toggle sections
    if ('webVitals' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        webvitals: typeof updatedConfig.webVitals === 'boolean' ? updatedConfig.webVitals : true
      };
    }

    if ('websocket' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        websocket: typeof updatedConfig.websocket === 'boolean' ? updatedConfig.websocket : false
      };
    }

    if ('socketio' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        socketio: typeof updatedConfig.socketio === 'boolean' ? updatedConfig.socketio : false
      };
    }

    // Sync instrumentations changes back to main config
    if ('instrumentations' in newConfig && newConfig.instrumentations) {
      if ('webvitals' in newConfig.instrumentations) {
        updatedConfig.webVitals = typeof newConfig.instrumentations.webvitals === 'boolean' ? newConfig.instrumentations.webvitals : true;
      }
      if ('websocket' in newConfig.instrumentations) {
        updatedConfig.websocket = typeof newConfig.instrumentations.websocket === 'boolean' ? newConfig.instrumentations.websocket : false;
      }
      if ('socketio' in newConfig.instrumentations) {
        updatedConfig.socketio = typeof newConfig.instrumentations.socketio === 'boolean' ? newConfig.instrumentations.socketio : false;
      }
    }

    console.log('Final updated config:', updatedConfig);
    setConfig(updatedConfig);

    try {
      const configString = JSON.stringify(updatedConfig);
      localStorage.setItem('imqaConfig', configString);
      console.log('Config updated and saved to localStorage successfully');
    } catch (error) {
      console.error('Failed to save updated config to localStorage:', error);
    }

    // Check if we can initialize IMQA with the updated config
    const hasRequiredValues = !!(
      updatedConfig.collectorUrl &&
      updatedConfig.serviceName &&
      updatedConfig.serviceVersion &&
      updatedConfig.serviceKey
    );

    console.log('After update - required values check:', {
      collectorUrl: !!updatedConfig.collectorUrl,
      serviceName: !!updatedConfig.serviceName,
      serviceVersion: !!updatedConfig.serviceVersion,
      serviceKey: !!updatedConfig.serviceKey,
      hasAllRequired: hasRequiredValues,
      currentlyInitialized: isInitialized
    });

    if (IMQA) {
      if (isInitialized) {
        // Already initialized - need to reinitialize
        console.log('IMQA already initialized, reinitializing with new config...');
        deinitIMQA();
        setTimeout(() => {
          initIMQA();
        }, 100);
      } else if (hasRequiredValues) {
        // Not initialized yet, but now we have all required values
        console.log('IMQA not initialized yet, initializing with new config...');
        setTimeout(() => {
          initIMQA();
        }, 100);
      }
    }
  };

  // Initialize IMQA with the current config
  const initIMQA = () => {
    if (!IMQA) {
      console.warn('IMQA module not loaded yet');
      return;
    }

    try {
      // Ensure we have required values
      if (
        !config.collectorUrl ||
        !config.serviceName ||
        !config.serviceVersion ||
        !config.serviceKey
      ) {
        console.error('Missing required IMQA configuration:', {
          collectorUrl: !!config.collectorUrl,
          serviceName: !!config.serviceName,
          serviceVersion: !!config.serviceVersion,
          serviceKey: !!config.serviceKey
        });
        return;
      }

      // Check if already initialized
      if (IMQA.inited) {
        console.warn('IMQA already initialized, deinitializing first...');
        IMQA.deinit();
      }

      // Initialize IMQA with current config
      IMQA.init(config);
      setIsInitialized(true);
      console.info('IMQA initialized successfully with config:', config);
    } catch (error) {
      console.error('Failed to initialize IMQA:', error);
      setIsInitialized(false);
    }
  };

  // Deinitialize IMQA
  const deinitIMQA = () => {
    if (!IMQA) {
      console.warn('IMQA module not loaded');
      return;
    }

    // Set guard to prevent auto-reinit useEffect from immediately re-initing
    manualDeinitRef.current = true;

    try {
      if (IMQA.inited) {
        IMQA.deinit();
        setIsInitialized(false);
        console.info('IMQA deinitialized successfully');
      } else {
        console.warn('IMQA was not initialized');
        setIsInitialized(false);
      }
    } catch (error) {
      console.error('Failed to deinitialize IMQA:', error);
      setIsInitialized(false);
    }
  };

  // Toggle a feature in the config
  const toggleFeature = (
    feature: keyof IMQASDKConfig,
    value: boolean | number | string,
  ) => {
    updateConfig({ [feature]: value });
  };

  // Set autoReinit and persist to localStorage
  const setAutoReinit = (value: boolean) => {
    setAutoReinitState(value);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('imqaAutoReinit', JSON.stringify(value));
        console.log('Auto-reinit setting saved:', value);
      } catch (error) {
        console.warn('Failed to save autoReinit to localStorage:', error);
      }
    }
  };

  // Reset config to defaults and clear localStorage
  const resetToDefaults = () => {
    console.log('Resetting IMQA config to defaults');

    // Deinitialize IMQA if initialized
    if (IMQA && isInitialized) {
      try {
        IMQA.deinit();
        setIsInitialized(false);
        console.log('IMQA deinitialized during reset');
      } catch (error) {
        console.error('Failed to deinitialize IMQA during reset:', error);
      }
    }

    // Clear localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('imqaConfig');
        console.log('Cleared imqaConfig from localStorage');
      } catch (error) {
        console.warn('Failed to clear localStorage:', error);
      }
    }

    // Reset config state
    setConfig(DEFAULT_IMQA_CONFIG);
    console.log('Config reset to defaults');
  };

  // Clear localStorage without resetting state
  const clearLocalStorage = () => {
    console.log('Clearing IMQA config from localStorage only');
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('imqaConfig');
        console.log('Cleared imqaConfig from localStorage');
      } catch (error) {
        console.warn('Failed to clear localStorage:', error);
      }
    }
  };

  return (
    <IMQAContext.Provider
      value={{
        config,
        isInitialized,
        webViewParams,
        autoReinit,
        updateConfig,
        initIMQA,
        deinitIMQA,
        toggleFeature,
        setAutoReinit,
        resetToDefaults,
        clearLocalStorage,
      }}
    >
      {children}
    </IMQAContext.Provider>
  );
};
